import { LINE_LEVELS, type LineLevel } from '../../../shared/types';

/**
 * Which levels of an opening you want scheduled, as a set rather than a floor.
 *
 * The original level picker was cumulative: choosing "advanced" always dragged
 * in everything beneath it. That cannot express "intermediate and above, but
 * not beginner" — a completely reasonable thing to want once the beginner lines
 * are known cold — so this replaces it with one checkbox per level.
 *
 * Each box shows how many lines it would add, because "advanced" meaning six
 * more lines in the Italian and one in the Slav is the thing you actually want
 * to know before ticking it. Levels the opening has no lines at are shown
 * disabled rather than hidden, so the ladder's shape stays visible.
 */

const BLURB: Record<LineLevel, string> = {
  opening: 'Just the opening moves — the sequence every variation starts from',
  beginner: 'The main lines — enough to play the opening',
  intermediate: 'The variations you will actually meet',
  advanced: 'Sharper sidelines and the critical tries',
  master: 'The deepest theory',
};

export default function LevelBandPicker({
  counts,
  selectedByLevel,
  onChange,
  disabled = false,
  compact = false,
}: {
  /** Lines available at each level. Absent means the opening has none. */
  counts: Partial<Record<LineLevel, number>>;
  /** How many at each level are currently scheduled. */
  selectedByLevel: Partial<Record<LineLevel, number>>;
  onChange: (levels: LineLevel[]) => void;
  disabled?: boolean;
  compact?: boolean;
}) {
  const available = LINE_LEVELS.filter((l) => (counts[l] ?? 0) > 0);
  // A level counts as on when any of its lines are scheduled. Partial selections
  // are possible via per-line checkboxes, so this is deliberately permissive.
  const isOn = (l: LineLevel) => (selectedByLevel[l] ?? 0) > 0;
  const current = available.filter(isOn);

  function toggle(level: LineLevel) {
    const next = isOn(level) ? current.filter((l) => l !== level) : [...current, level];
    // The API refuses an empty repertoire entry, and silently sending one would
    // surface as a confusing error, so the last box cannot be unticked.
    if (next.length === 0) return;
    onChange(next);
  }

  const total = available.reduce((n, l) => n + (counts[l] ?? 0), 0);
  const on = available.reduce((n, l) => n + (selectedByLevel[l] ?? 0), 0);

  return (
    <div className={`bands${compact ? ' bands--compact' : ''}`}>
      <div className="bands-row" role="group" aria-label="Levels to study">
        {available.map((level) => {
          const active = isOn(level);
          const partial = active && (selectedByLevel[level] ?? 0) < (counts[level] ?? 0);
          return (
            <button
              key={level}
              type="button"
              className={[
                'band',
                active ? 'band--on' : '',
                partial ? 'band--partial' : '',
              ]
                .filter(Boolean)
                .join(' ')}
              aria-pressed={active}
              disabled={disabled || (active && current.length === 1)}
              onClick={() => toggle(level)}
              title={
                active && current.length === 1
                  ? 'Keep at least one level — remove the opening instead'
                  : BLURB[level]
              }
            >
              <span className={`level-dot level-dot--${level}`} aria-hidden="true" />
              <span className="band-name">{level}</span>
              <span className="band-count">
                {partial ? `${selectedByLevel[level]}/${counts[level]}` : counts[level]}
              </span>
            </button>
          );
        })}
      </div>
      {!compact && (
        <p className="muted band-summary">
          Studying <strong>{on}</strong> of {total} variations.
        </p>
      )}
    </div>
  );
}
